import type { ComponentProps } from "react"
import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Card from "@/v2-ui/card"
import Input from "@/v2-ui/input"
import { toast } from "@/v2-ui/toast"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useUserSharingFlagQuery } from "@/v2-backoffice-client/user/sharingFlag/user.sharingFlag.query"
import { useSetUserSharingFlagMutation } from "@/v2-backoffice-client/user/sharingFlag/user.sharingFlag.mutation"

export type SharingFlagCardProps = ComponentProps<"div"> & {
  userId: number
}

// SharingFlagCard lets an operator flag/unflag a user for single-active-
// session enforcement, beside the read-only sharing-risk score. Flagging
// is the manual half of PM-21; the score informs the decision but is not
// changed here.
function SharingFlagCard(props: SharingFlagCardProps) {
  const { userId, className, ...divProps } = props
  const [reason, setReason] = useState("")

  const { data, loading, refetch } = useUserSharingFlagQuery({
    variables: { userId },
  })
  const [setFlag, { loading: saving }] = useSetUserSharingFlagMutation()

  const flag = data?.getUserSharingFlag ?? null
  const isEnforced = Boolean(flag?.IsEnforced)
  const busy = loading || saving

  function handleToggle(enforce: boolean) {
    setFlag({
      variables: { userId, enforce, reason: reason.trim() || null },
    })
      .then(() => {
        toast({
          title: enforce
            ? "User flagged for single active session"
            : "Sharing flag removed",
        })
        setReason("")
        return refetch()
      })
      .catch((error) => {
        toast({
          title: "Could not update the sharing flag",
          description: error instanceof Error ? error.message : undefined,
        })
      })
  }

  return (
    <Card className={cn("sharing-flag-card", className)} {...divProps}>
      <Card.Header>
        <Card.Title className="flex items-center gap-2 font-medium">
          Single active session:{" "}
          <DotText variant={isEnforced ? "destructive" : "secondary"}>
            {isEnforced ? "Enforced" : "Not enforced"}
          </DotText>
        </Card.Title>
        <Card.Description>
          Flagged users can only be signed in on one device at a time. On a
          second device they get a choice screen instead of a silent login.
        </Card.Description>
      </Card.Header>

      <Card.Content className="flex flex-col gap-2">
        {isEnforced && flag?.Reason ? (
          <p className="text-sm text-muted-foreground">Reason: {flag.Reason}</p>
        ) : null}

        {isEnforced ? (
          <ConfirmButton
            className="ml-auto"
            variant="warning"
            size="sm"
            content="Remove single-active-session enforcement"
            confirmContent="Remove the single-active-session flag? The user will be able to sign in on multiple devices again."
            confirmVariant="warning"
            isLoading={saving}
            disabled={busy}
            onConfirm={() => handleToggle(false)}
          >
            Unflag login
          </ConfirmButton>
        ) : (
          <>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              disabled={busy}
            />
            <ConfirmButton
              className="ml-auto"
              variant="warning"
              size="sm"
              content="Enforce single active session"
              confirmContent="Flag this user for single active session? On a second device they get a choice screen instead of a silent login."
              confirmVariant="warning"
              isLoading={saving}
              disabled={busy}
              onConfirm={() => handleToggle(true)}
            >
              Flag login
            </ConfirmButton>
          </>
        )}
      </Card.Content>
    </Card>
  )
}

export default SharingFlagCard
